import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { catalog, getPage } from '../content'
import { media } from '../content/media'
import { manualUi } from '../i18n/messages'
import { useManualLocale } from '../i18n/LocaleContext'
import { MANUAL_HOME, manualPagePath } from '../paths'

interface SidebarProps {
  open: boolean
  onNavigate?: () => void
  onOpenSearch: () => void
}

function pageIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/docs\/x-translator\/([^/]+)/)
  return match?.[1] ?? null
}

export function Sidebar({ open, onNavigate, onOpenSearch }: SidebarProps) {
  const { locale } = useManualLocale()
  const ui = manualUi(locale)
  const navigation = catalog(locale).navigation
  const location = useLocation()
  const activePageId = pageIdFromPath(location.pathname)
  const activeHash = location.hash.replace(/^#/, '')

  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set(),
  )
  const [expandedPages, setExpandedPages] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    if (!activePageId) return
    setExpandedPages((prev) => {
      if (prev.has(activePageId)) return prev
      const next = new Set(prev)
      next.add(activePageId)
      return next
    })
    const category = navigation.find((c) =>
      c.pages.some((p) => p.id === activePageId),
    )
    if (category) {
      setCollapsedCategories((prev) => {
        if (!prev.has(category.id)) return prev
        const next = new Set(prev)
        next.delete(category.id)
        return next
      })
    }
  }, [activePageId, navigation])

  function toggleCategory(id: string) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function togglePage(id: string) {
    setExpandedPages((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <aside className={`sidebar${open ? ' open' : ''}`} aria-label="Documentation navigation">
      <Link to={MANUAL_HOME} className="sidebar-brand" onClick={onNavigate}>
        <img
          className="brand-mark"
          src={media.sepmLogo}
          alt=""
          width={42}
          height={42}
        />
        <div>
          <p className="brand-line">
            <span className="brand-sepm">SEPM</span>
            <span className="brand-sep">·</span>
            <span className="brand-product">X-Translator</span>
          </p>
          <p className="brand-sub">{ui.manual}</p>
        </div>
      </Link>

      <button type="button" className="search-trigger" onClick={onOpenSearch}>
        <span>{ui.searchDocs}</span>
        <kbd>Ctrl K</kbd>
      </button>

      <nav className="sidebar-nav">
        {navigation.map((category) => {
          const categoryOpen = !collapsedCategories.has(category.id)
          return (
            <div
              key={category.id}
              className={`nav-category${categoryOpen ? ' is-open' : ''}`}
            >
              <button
                type="button"
                className="nav-category-toggle"
                aria-expanded={categoryOpen}
                onClick={() => toggleCategory(category.id)}
              >
                <span className="nav-chevron" aria-hidden="true" />
                <span>{category.title}</span>
              </button>

              {categoryOpen ? (
                <ul className="nav-page-list">
                  {category.pages.map((page) => {
                    const doc = getPage(page.id, locale)
                    const sections = doc?.sections ?? []
                    const hasSections = sections.length > 1
                    const pageExpanded = expandedPages.has(page.id)

                    return (
                      <li
                        key={page.id}
                        className={`nav-page-item${pageExpanded && hasSections ? ' is-expanded' : ''}`}
                      >
                        <div className="nav-page-row">
                          {hasSections ? (
                            <button
                              type="button"
                              className="nav-page-expand"
                              aria-expanded={pageExpanded}
                              aria-label={(pageExpanded ? ui.collapseSections : ui.expandSections).replace(
                                '{title}',
                                page.title,
                              )}
                              onClick={() => togglePage(page.id)}
                            >
                              <span className="nav-chevron" aria-hidden="true" />
                            </button>
                          ) : (
                            <span className="nav-page-spacer" aria-hidden="true" />
                          )}
                          <NavLink
                            to={manualPagePath(page.id)}
                            end
                            onClick={onNavigate}
                            className={({ isActive }) =>
                              `nav-page-link${isActive ? ' active' : ''}`
                            }
                          >
                            {page.title}
                          </NavLink>
                        </div>

                        {hasSections && pageExpanded ? (
                          <ul className="nav-section-list">
                            {sections.map((section) => {
                              const isActiveSection =
                                activePageId === page.id && activeHash === section.id
                              return (
                                <li key={section.id}>
                                  <Link
                                    to={manualPagePath(page.id, section.id)}
                                    className={`nav-section-link${isActiveSection ? ' active' : ''}`}
                                    onClick={onNavigate}
                                  >
                                    {section.title}
                                  </Link>
                                </li>
                              )
                            })}
                          </ul>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </div>
          )
        })}
      </nav>
    </aside>
  )
}
